import React, { useEffect, useRef } from 'react';
import { FiTarget } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const FuelGauge = ({ percentage, title, subtitle }) => {
  // Define color thresholds with simplified colors
  const getColor = (percent) => {
    if (percent >= 100) return '#16a34a'; // Green
    if (percent >= 75) return '#22c55e'; // Light green
    if (percent >= 50) return '#f59e0b'; // Amber
    if (percent >= 25) return '#d97706'; // Dark amber
    return '#dc2626'; // Red
  };

  // Display percentage can exceed 100% for visual indication
  const displayPercentage = Math.max(0, percentage);
  const cappedPercentage = Math.min(100, percentage);

  // For progress bar
  const gaugeColor = getColor(percentage >= 100 ? 100 : percentage);

  // For odometer animation
  const odometerRef = useRef(null);
  const previousPercentage = useRef(displayPercentage);

  useEffect(() => {
    const odometer = odometerRef.current;
    if (!odometer) return;

    // Animate the odometer
    const duration = 1500; // animation duration in ms
    const start = previousPercentage.current;
    const end = displayPercentage;
    const range = end - start;
    const startTime = performance.now();

    const animateOdometer = (timestamp) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
      const easedProgress = easeOutQuart(progress);

      // Update the counter value
      const currentValue = Math.round(start + range * easedProgress);

      // Format to 3 digits with padding
      const digits = String(Math.min(999, currentValue)).padStart(3, '0').split('');
      const digitElements = odometer.querySelectorAll('.digit-value');

      digits.forEach((digit, index) => {
        if (digitElements[index]) {
          digitElements[index].textContent = digit;
          digitElements[index].style.color = gaugeColor;
        }
      });

      if (progress < 1) {
        requestAnimationFrame(animateOdometer);
      }
    };

    requestAnimationFrame(animateOdometer);
    previousPercentage.current = displayPercentage;
  }, [displayPercentage, gaugeColor]);

  // Get status classes based on percentage
  const getStatusClasses = () => {
    if (percentage >= 100)
      return {
        bg: "bg-green-50 dark:bg-green-900/30",
        text: "text-green-800 dark:text-green-300",
        textBold: "text-green-700 dark:text-green-400",
        border: "border-green-200 dark:border-green-800/50"
      };
    if (percentage >= 75)
      return {
        bg: "bg-green-50 dark:bg-green-900/30",
        text: "text-green-800 dark:text-green-300",
        textBold: "text-green-700 dark:text-green-400",
        border: "border-green-200 dark:border-green-800/50"
      };
    if (percentage >= 50)
      return {
        bg: "bg-amber-50 dark:bg-amber-900/30",
        text: "text-amber-800 dark:text-amber-300",
        textBold: "text-amber-700 dark:text-amber-400",
        border: "border-amber-200 dark:border-amber-800/50"
      };
    return {
      bg: "bg-red-50 dark:bg-red-900/30",
      text: "text-red-800 dark:text-red-300",
      textBold: "text-red-700 dark:text-red-400",
      border: "border-red-200 dark:border-red-800/50"
    };
  };

  const statusClasses = getStatusClasses();

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          {subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        <div className="text-xl font-bold" style={{ color: gaugeColor }}>
          {Math.round(displayPercentage)}%
        </div>
      </div>

      {/* Odometer display */}
      <div className="flex flex-col items-center justify-center">
        <div className="relative w-full">
          {/* Above goal indicator */}
          {percentage > 100 && (
            <div className="absolute -top-6 right-0 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-1 rounded-full text-xs font-semibold border border-green-200 dark:border-green-800/50 whitespace-nowrap">
              {Math.round(percentage - 100)}% above goal
            </div>
          )}

          {/* Updated odometer with minimalist style */}
          <div
            ref={odometerRef}
            className="flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-6 px-4 rounded-xl shadow-lg w-full mb-4 border border-gray-100 dark:border-gray-700"
            style={{
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1),0 2px 4px -1px rgba(0,0,0,0.06),inset 0 2px 4px rgba(0,0,0,0.06)'
            }}
          >
            {/* Digit slots */}
            <div className="flex items-center space-x-1">
              {/* Hundreds */}
              <div className="relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-inner">
                <div className="digit-slot">
                  <div
                    className="digit-value text-4xl md:text-5xl font-bold text-center w-12 md:w-14 py-2"
                    style={{ color: gaugeColor }}
                  >
                    {String(Math.min(999, Math.round(displayPercentage))).padStart(3, '0')[0]}
                  </div>
                </div>
              </div>

              {/* Tens */}
              <div className="relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-inner">
                <div className="digit-slot">
                  <div
                    className="digit-value text-4xl md:text-5xl font-bold text-center w-12 md:w-14 py-2"
                    style={{ color: gaugeColor }}
                  >
                    {String(Math.min(999, Math.round(displayPercentage))).padStart(3, '0')[1]}
                  </div>
                </div>
              </div>

              {/* Units */}
              <div className="relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-inner">
                <div className="digit-slot">
                  <div
                    className="digit-value text-4xl md:text-5xl font-bold text-center w-12 md:w-14 py-2"
                    style={{ color: gaugeColor }}
                  >
                    {String(Math.min(999, Math.round(displayPercentage))).padStart(3, '0')[2]}
                  </div>
                </div>
              </div>

              {/* Percentage symbol */}
              <div className="text-3xl md:text-4xl font-bold ml-1" style={{ color: gaugeColor }}>%</div>
            </div>
          </div>
        </div>

        {/* Progress bar below odometer */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
          <div
            className="h-3 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${cappedPercentage}%`, backgroundColor: gaugeColor }}
          />
        </div>

        {/* Goal marker */}
        <div className="relative w-full h-6">
          <div className="absolute top-0 left-0 right-0 flex justify-between">
            <div className="text-xs font-medium text-red-700 dark:text-red-400">0%</div>
            <div className="text-xs font-medium text-amber-700 dark:text-amber-400">50%</div>
            <div className="text-xs font-medium text-green-700 dark:text-green-400">100%</div>
          </div>
          {percentage > 100 && (
            <div
              className="absolute top-0 h-6 border-r-2 border-dashed border-green-500"
              style={{ left: '100%', marginLeft: '-1px' }}
            >
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></div>
            </div>
          )}
        </div>
      </div>

      {/* Goal indicator */}
      <div className="flex justify-center mt-4">
        <div className={`${statusClasses.bg} px-4 py-2 rounded-lg inline-flex items-center ${statusClasses.border}`}>
          <SafeIcon icon={FiTarget} className={`w-4 h-4 mr-2 ${statusClasses.textBold}`} />
          <span className={`text-sm font-medium ${statusClasses.text}`}>
            {percentage >= 100 ? `Goal achieved! (${Math.round(displayPercentage)}%)` : `Goal Progress: ${Math.round(displayPercentage)}%`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FuelGauge;